// Drill-down im Mega-Menü für große Rubriken (snippets/fk-mega-panel.liquid): links die Kategorien, in der Mitte
// die Links der gewählten Kategorie. Die gewählte Kategorie trägt `aria-current="true"` (und `aria-controls` auf ihr
// Pane); das CSS zeigt das Pane direkt dahinter. Bewusst kein `aria-expanded`: Horizon öffnet Untermenüs per
// `.menu-list__list-item:has([aria-expanded='true'])` (blocks/_header-menu.liquid).
//
// - Maus: Überfahren wählt. Bewegt sich der Zeiger schräg in Richtung Linkspalte, wartet die Auswahl kurz
//   (wie „menu aim“), damit man beim Hinübergleiten nicht die Kategorien darunter erwischt.
// - Tastatur: Fokus wählt (Tab geht Kategorie → ihre Links → nächste Kategorie, Umschalt+Tab entsprechend zurück),
//   Pfeil hoch/runter springt zwischen den Kategorien, Pfeil rechts in die Links, Pfeil links zurück. Escape schließt
//   das Panel wie bisher über Horizons assets/header-menu.js.
// - Touch auf Geräten, die das Mega-Menü zeigen (Horizon nimmt bei echten Touch-Geräten ohnehin die Schublade):
//   erster Tipp wählt, zweiter Tipp öffnet die Kategorie.
// - Beim erneuten Öffnen des Panels ist wieder die erste Kategorie gewählt.

const AIM_DELAY_MS = 280;
const AIM_TOLERANCE_PX = 60;
const TRACKED_POSITIONS = 3;

class FkMegaDrill extends HTMLElement {
  /** @type {HTMLAnchorElement | null} */
  #current = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  /** @type {{ x: number, y: number }[]} */
  #positions = [];

  /** @type {{ x: number, y: number } | null} */
  #lastDelayPosition = null;

  /** @type {string} */
  #lastPointerType = '';

  /** @type {MutationObserver | null} */
  #observer = null;

  connectedCallback() {
    this.#current = this.groups.find((group) => group.getAttribute('aria-current') === 'true') ?? null;
    if (!this.#current && this.groups[0]) this.select(this.groups[0]);

    this.addEventListener('pointerover', this.#onPointerOver);
    this.addEventListener('pointermove', this.#onPointerMove);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    this.addEventListener('pointerdown', this.#onPointerDown);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('focusin', this.#onFocusIn);
    this.addEventListener('keydown', this.#onKeydown);

    // Horizon schaltet `inert` am Untermenü beim Öffnen und Schließen (assets/header-menu.js)
    const submenu = this.closest('.menu-list__submenu');
    if (submenu) {
      this.#observer = new MutationObserver(() => {
        if (submenu.hasAttribute('inert')) {
          this.#cancel();
        } else if (!this.contains(document.activeElement)) {
          this.#positions = [];
          if (this.groups[0]) this.select(this.groups[0]);
        }
      });
      this.#observer.observe(submenu, { attributes: true, attributeFilter: ['inert'] });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('pointerover', this.#onPointerOver);
    this.removeEventListener('pointermove', this.#onPointerMove);
    this.removeEventListener('pointerleave', this.#onPointerLeave);
    this.removeEventListener('pointerdown', this.#onPointerDown);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('focusin', this.#onFocusIn);
    this.removeEventListener('keydown', this.#onKeydown);
    this.#observer?.disconnect();
    this.#observer = null;
    this.#cancel();
  }

  /** @returns {HTMLAnchorElement[]} */
  get groups() {
    return /** @type {HTMLAnchorElement[]} */ ([...this.querySelectorAll('[data-fk-drill-group]')]);
  }

  /**
   * Zeigt die Links einer Kategorie.
   * @param {HTMLAnchorElement} group
   */
  select(group) {
    this.#cancel();
    if (group === this.#current) return;
    for (const item of this.groups) {
      if (item === group) item.setAttribute('aria-current', 'true');
      else item.removeAttribute('aria-current');
    }
    this.#current = group;
  }

  /**
   * @param {HTMLAnchorElement} group
   * @returns {HTMLElement | null}
   */
  #paneFor(group) {
    const id = group.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  }

  /** @param {EventTarget | null} target */
  #groupFrom(target) {
    const group = target instanceof Element ? target.closest('[data-fk-drill-group]') : null;
    return group instanceof HTMLAnchorElement && this.contains(group) ? group : null;
  }

  #cancel() {
    clearTimeout(this.#timer);
    this.#timer = undefined;
  }

  /** @param {PointerEvent} event */
  #onPointerOver = (event) => {
    if (event.pointerType === 'touch') return;
    this.#track(event);
    const group = this.#groupFrom(event.target);
    if (group) {
      this.#maybeSelect(group);
    } else if (this.#current && this.#paneFor(this.#current)?.contains(/** @type {Node} */ (event.target))) {
      // Zeiger ist in der Linkspalte angekommen: Auswahl bleibt
      this.#cancel();
    }
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (event.pointerType === 'touch') return;
    this.#track(event);
  };

  /** @param {PointerEvent} event */
  #track(event) {
    const last = this.#positions[this.#positions.length - 1];
    if (last && last.x === event.clientX && last.y === event.clientY) return;
    this.#positions.push({ x: event.clientX, y: event.clientY });
    if (this.#positions.length > TRACKED_POSITIONS) this.#positions.shift();
  }

  #onPointerLeave = () => {
    this.#cancel();
    this.#positions = [];
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    this.#lastPointerType = event.pointerType;
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const pointerType = this.#lastPointerType;
    this.#lastPointerType = '';
    const group = this.#groupFrom(event.target);
    if (!group || pointerType !== 'touch' || group === this.#current) return;
    // Touch: erster Tipp zeigt die Links, der zweite folgt dem Link
    event.preventDefault();
    this.select(group);
  };

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    const group = this.#groupFrom(event.target);
    if (!group) return;
    // Nur Tastaturfokus wählt; ein Tipp fokussiert in manchen Browsern den Link schon vor dem click
    if (this.#lastPointerType === 'touch') return;
    this.select(group);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    this.#lastPointerType = '';
    const groups = this.groups;
    const group = this.#groupFrom(event.target);

    // Umschalt+Tab von einer Kategorie: zurück in die Links der vorherigen Kategorie statt über sie hinweg
    if (event.key === 'Tab' && event.shiftKey && !event.altKey && !event.ctrlKey && !event.metaKey) {
      const index = group ? groups.indexOf(group) : -1;
      const previous = index > 0 ? groups[index - 1] : null;
      if (previous) {
        this.select(previous);
        const links = this.#paneFor(previous)?.querySelectorAll('a[href]');
        const last = links?.[links.length - 1];
        if (last) {
          event.preventDefault();
          last.focus();
        }
      }
      return;
    }

    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;

    if (group) {
      const index = groups.indexOf(group);
      /** @type {HTMLElement | null | undefined} */
      let next = null;
      switch (event.key) {
        case 'ArrowDown':
          next = groups[(index + 1) % groups.length];
          break;
        case 'ArrowUp':
          next = groups[(index - 1 + groups.length) % groups.length];
          break;
        case 'Home':
          next = groups[0];
          break;
        case 'End':
          next = groups[groups.length - 1];
          break;
        case 'ArrowRight':
          this.select(group);
          next = this.#paneFor(group)?.querySelector('a[href]');
          break;
      }
      if (next) {
        event.preventDefault();
        next.focus();
      }
      return;
    }

    if (event.key === 'ArrowLeft' && this.#current && event.target instanceof Node) {
      if (this.#paneFor(this.#current)?.contains(event.target)) {
        event.preventDefault();
        this.#current.focus();
      }
    }
  };

  /**
   * Wählt sofort – oder etwas später, wenn der Zeiger gerade schräg Richtung Linkspalte unterwegs ist.
   * @param {HTMLAnchorElement} group
   */
  #maybeSelect(group) {
    this.#cancel();
    if (group === this.#current) return;
    const delay = this.#aimDelay();
    if (delay > 0) {
      this.#timer = setTimeout(() => this.#maybeSelect(group), delay);
    } else {
      this.select(group);
    }
  }

  /** @returns {number} */
  #aimDelay() {
    const pane = this.#current ? this.#paneFor(this.#current) : null;
    const positions = this.#positions;
    if (!pane || positions.length < 2) return 0;

    const position = positions[positions.length - 1];
    const previous = positions[0];
    if (!position || !previous) return 0;
    const lastDelay = this.#lastDelayPosition;
    if (lastDelay && lastDelay.x === position.x && lastDelay.y === position.y) return 0;

    const paneRect = pane.getBoundingClientRect();
    const rect = this.getBoundingClientRect();
    const left = paneRect.left;
    if (position.x >= left || previous.x >= left) return 0;

    const upper = { x: left, y: rect.top - AIM_TOLERANCE_PX };
    const lower = { x: left, y: rect.bottom + AIM_TOLERANCE_PX };
    /** @param {{ x: number, y: number }} a @param {{ x: number, y: number }} b */
    const slope = (a, b) => (b.y - a.y) / (b.x - a.x);

    // Liegt die aktuelle Position im Dreieck zwischen der vorherigen Position und der linken Kante der Linkspalte?
    if (slope(position, upper) < slope(previous, upper) && slope(position, lower) > slope(previous, lower)) {
      this.#lastDelayPosition = position;
      return AIM_DELAY_MS;
    }
    this.#lastDelayPosition = null;
    return 0;
  }
}

if (!customElements.get('fk-mega-drill')) {
  customElements.define('fk-mega-drill', FkMegaDrill);
}
